import { useState } from 'react';
import AdminLayout from '../components/AdminLayout';
import { useTheme } from '../context/ThemeContext';
import { useCampaign, Campaign, CampaignTarget } from '@/context/CampaignContext';
import { useCelebration, SpecialDay } from '@/context/CelebrationContext';
import CampaignFormModal from './CampaignFormModal';
import SpecialDayFormModal from './SpecialDayFormModal';
import useScrollLock from '@/hooks/useScrollLock';

type PageTab = 'campaigns' | 'special-days';

/* ─── Campaigns Tab ─── */
function CampaignsTab({ dark }: { dark: boolean }) {
  const { campaigns, deleteCampaign, toggleActive } = useCampaign();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Campaign | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  useScrollLock(Boolean(deleteConfirm));

  const card = dark ? 'bg-[#1C2B3A] border border-white/5' : 'bg-white border border-gray-200';
  const text = dark ? 'text-white' : 'text-gray-900';
  const muted = dark ? 'text-white/50' : 'text-gray-500';
  const divider = dark ? 'divide-white/5' : 'divide-gray-100';
  const borderCls = dark ? 'border-white/10' : 'border-gray-100';

  const now = new Date();
  const getStatus = (c: Campaign) => {
    if (!c.active) return { label: 'Inactive', color: 'bg-gray-500/20 text-gray-400' };
    const start = new Date(c.startDate);
    const end = new Date(c.endDate);
    if (now < start) return { label: 'Scheduled', color: 'bg-sky-500/20 text-sky-400' };
    if (now > end) return { label: 'Expired', color: 'bg-red-500/20 text-red-400' };
    return { label: 'Live', color: 'bg-emerald-500/20 text-emerald-400' };
  };

  const targetLabel: Record<CampaignTarget, string> = {
    all: 'All Products',
    lithium: 'Lithium Series',
    agm: 'AGM Series',
    gel: 'Gel Series',
    specific: 'Specific Products',
    category_and_specific: 'Category + Specific',
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-4">
        <p className={`text-sm ${muted}`}>Create discount campaigns and apply them to product prices automatically.</p>
        <button onClick={() => { setEditing(null); setModalOpen(true); }} className="flex items-center gap-2 bg-[#F97316] text-white text-sm font-bold px-4 py-2 rounded-lg hover:bg-[#ea6a0a] transition-colors cursor-pointer whitespace-nowrap">
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line"></i></div>
          New Campaign
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Total', value: campaigns.length, icon: 'ri-megaphone-line', color: 'text-[#F97316]' },
          { label: 'Live Now', value: campaigns.filter((c) => getStatus(c).label === 'Live').length, icon: 'ri-live-line', color: 'text-emerald-400' },
          { label: 'Scheduled', value: campaigns.filter((c) => getStatus(c).label === 'Scheduled').length, icon: 'ri-calendar-schedule-line', color: 'text-sky-400' },
        ].map((s) => (
          <div key={s.label} className={`${card} rounded-lg p-4 flex items-center gap-4`}>
            <div className={`w-10 h-10 flex items-center justify-center rounded-full shrink-0 ${s.color} bg-current/10`}>
              <i className={`${s.icon} text-lg ${s.color}`}></i>
            </div>
            <div>
              <p className={`text-2xl font-black ${text}`}>{s.value}</p>
              <p className={`text-xs ${muted}`}>{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className={`${card} rounded-lg overflow-hidden`}>
        <div className={`grid grid-cols-12 gap-3 px-5 py-3 text-xs font-bold uppercase tracking-wide ${muted} border-b ${borderCls}`}>
          <div className="col-span-3">Campaign</div>
          <div className="col-span-2">Discount</div>
          <div className="col-span-2">Target</div>
          <div className="col-span-2">Period</div>
          <div className="col-span-1 text-center">Status</div>
          <div className="col-span-1 text-center">Active</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        <div className={`divide-y ${divider}`}>
          {campaigns.length === 0 ? (
            <div className={`text-center py-16 ${muted}`}>
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3"><i className="ri-megaphone-line text-4xl"></i></div>
              <p className="text-sm font-medium mb-1">No campaigns yet</p>
              <p className="text-xs">Create your first discount campaign to get started.</p>
            </div>
          ) : campaigns.map((c) => {
            const status = getStatus(c);
            return (
              <div key={c.id} className={`grid grid-cols-12 gap-3 px-5 py-4 items-center transition-colors ${dark ? 'hover:bg-white/3' : 'hover:bg-gray-50'}`}>
                <div className="col-span-3">
                  <p className={`text-sm font-semibold ${text}`}>{c.name}</p>
                  <p className={`text-xs ${muted} mt-0.5 truncate`}>{c.description || '—'}</p>
                  {c.badgeLabel && <span className="inline-block mt-1 text-[10px] font-bold bg-[#F97316]/20 text-[#F97316] px-2 py-0.5 rounded-full">{c.badgeLabel}</span>}
                </div>
                <div className="col-span-2">
                  <span className={`text-base font-black ${text}`}>{c.discountType === 'percentage' ? `${c.discountValue}% OFF` : `$${c.discountValue} OFF`}</span>
                </div>
                <div className={`col-span-2 text-sm ${muted}`}>
                  {targetLabel[c.target]}
                  {c.target === 'specific' && <span className="block text-xs mt-0.5">{c.productIds.length} product(s)</span>}
                  {c.target === 'category_and_specific' && (
                    <span className="block text-xs mt-0.5">{(c.targetCategories || []).join(', ')}{c.productIds.length > 0 ? ` + ${c.productIds.length} specific` : ''}</span>
                  )}
                </div>
                <div className={`col-span-2 text-xs ${muted}`}>
                  <span>{new Date(c.startDate).toLocaleDateString()}</span>
                  <span className="mx-1">→</span>
                  <span>{new Date(c.endDate).toLocaleDateString()}</span>
                </div>
                <div className="col-span-1 flex justify-center">
                  <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${status.color}`}>{status.label}</span>
                </div>
                <div className="col-span-1 flex justify-center">
                  <button onClick={() => toggleActive(c.id)} className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${c.active ? 'bg-[#F97316]' : dark ? 'bg-white/20' : 'bg-gray-200'}`}>
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${c.active ? 'left-5' : 'left-0.5'}`}></span>
                  </button>
                </div>
                <div className="col-span-1 flex items-center justify-end gap-1">
                  <button onClick={() => { setEditing(c); setModalOpen(true); }} className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${dark ? 'text-white/50 hover:text-white hover:bg-white/10' : 'text-gray-400 hover:text-gray-900 hover:bg-gray-100'}`}>
                    <i className="ri-edit-line text-sm"></i>
                  </button>
                  <button onClick={() => setDeleteConfirm(c.id)} className="w-8 h-8 flex items-center justify-center rounded-lg text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors cursor-pointer">
                    <i className="ri-delete-bin-line text-sm"></i>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className={`${dark ? 'bg-[#1C2B3A]' : 'bg-white'} rounded-lg p-6 w-80`}>
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-red-500/20 mx-auto mb-4"><i className="ri-delete-bin-line text-red-400 text-2xl"></i></div>
            <h3 className={`text-center font-bold text-base mb-2 ${text}`}>Delete Campaign?</h3>
            <p className={`text-center text-sm ${muted} mb-6`}>This action cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className={`flex-1 py-2.5 rounded-lg text-sm font-semibold cursor-pointer border transition-colors ${dark ? 'border-white/20 text-white/70 hover:bg-white/10' : 'border-gray-200 text-gray-600 hover:bg-gray-100'}`}>Cancel</button>
              <button onClick={() => { deleteCampaign(deleteConfirm); setDeleteConfirm(null); }} className="flex-1 py-2.5 rounded-lg text-sm font-semibold bg-red-500 text-white hover:bg-red-600 transition-colors cursor-pointer">Delete</button>
            </div>
          </div>
        </div>
      )}
      {modalOpen && <CampaignFormModal campaign={editing} dark={dark} onClose={() => { setModalOpen(false); setEditing(null); }} />}
    </div>
  );
}

/* ─── Special Days Tab ─── */
function SpecialDaysTab({ dark }: { dark: boolean }) {
  const { specialDays, deleteSpecialDay, toggleSpecialDay, activeDay } = useCelebration();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<SpecialDay | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  useScrollLock(Boolean(deleteConfirm));

  const card = dark ? 'bg-[#1C2B3A] border border-white/5' : 'bg-white border border-gray-200';
  const text = dark ? 'text-white' : 'text-gray-900';
  const muted = dark ? 'text-white/50' : 'text-gray-500';
  const borderCls = dark ? 'border-white/10' : 'border-gray-100';

  const now = new Date();
  const getStatus = (d: SpecialDay) => {
    if (!d.active) return { label: 'Inactive', color: 'bg-gray-500/20 text-gray-400' };
    const start = new Date(d.startDate);
    const end = new Date(d.endDate);
    if (now < start) return { label: 'Upcoming', color: 'bg-sky-500/20 text-sky-400' };
    if (now > end) return { label: 'Ended', color: 'bg-red-500/20 text-red-400' };
    return { label: 'Live', color: 'bg-emerald-500/20 text-emerald-400' };
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-4">
        <p className={`text-sm ${muted}`}>Set up special day banners with animations that appear on the homepage.</p>
        <button onClick={() => { setEditing(null); setModalOpen(true); }} className="flex items-center gap-2 bg-[#F97316] text-white text-sm font-bold px-4 py-2 rounded-lg hover:bg-[#ea6a0a] transition-colors cursor-pointer whitespace-nowrap">
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line"></i></div>
          New Special Day
        </button>
      </div>

      {/* Active banner preview */}
      {activeDay && (
        <div className={`${card} rounded-lg overflow-hidden`}>
          <div className={`px-5 py-3 border-b ${borderCls} flex items-center gap-2`}>
            <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></div>
            <span className={`text-xs font-bold ${text}`}>Currently Live on Homepage</span>
          </div>
          <div
            className="px-5 py-3 flex items-center gap-4"
            style={{ background: `linear-gradient(135deg, ${activeDay.themeColor}22 0%, ${activeDay.themeColor}11 50%, ${activeDay.themeColor}22 100%)` }}
          >
            <span className="text-2xl">{activeDay.emoji}</span>
            <div className="flex-1">
              <p className="font-black text-sm" style={{ color: activeDay.themeColor }}>{activeDay.message}</p>
              {activeDay.subMessage && <p className={`text-xs mt-0.5 ${muted}`}>{activeDay.subMessage}</p>}
            </div>
            <span className="text-xs font-bold px-3 py-1 rounded-full text-white" style={{ background: activeDay.themeColor }}>Explore Products</span>
          </div>
        </div>
      )}

      {/* List */}
      <div className="space-y-3">
        {specialDays.length === 0 ? (
          <div className={`${card} rounded-lg text-center py-16 ${muted}`}>
            <div className="text-4xl mb-3">🎉</div>
            <p className="text-sm font-medium mb-1">No special days yet</p>
            <p className="text-xs">Create your first special day banner to celebrate with your visitors.</p>
          </div>
        ) : specialDays.map((d) => {
          const status = getStatus(d);
          return (
            <div key={d.id} className={`${card} rounded-lg overflow-hidden`}>
              {/* Color bar */}
              <div className="h-1 w-full" style={{ background: d.themeColor }}></div>
              <div className="p-4 flex items-center gap-4">
                <div className="text-3xl shrink-0">{d.emoji}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <p className={`font-bold text-sm ${text}`}>{d.name}</p>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${status.color}`}>{status.label}</span>
                    {!d.showBanner && <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-500/20 text-gray-400`}>Banner Hidden</span>}
                  </div>
                  <p className={`text-xs ${muted} truncate`}>{d.message}</p>
                  <p className={`text-xs ${muted} mt-0.5`}>
                    {new Date(d.startDate).toLocaleDateString()} → {new Date(d.endDate).toLocaleDateString()}
                    <span className="ml-2 capitalize">· {d.theme}</span>
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => toggleSpecialDay(d.id)} className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${d.active ? 'bg-[#F97316]' : dark ? 'bg-white/20' : 'bg-gray-200'}`}>
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${d.active ? 'left-5' : 'left-0.5'}`}></span>
                  </button>
                  <button onClick={() => { setEditing(d); setModalOpen(true); }} className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${dark ? 'text-white/50 hover:text-white hover:bg-white/10' : 'text-gray-400 hover:text-gray-900 hover:bg-gray-100'}`}>
                    <i className="ri-edit-line text-sm"></i>
                  </button>
                  <button onClick={() => setDeleteConfirm(d.id)} className="w-8 h-8 flex items-center justify-center rounded-lg text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors cursor-pointer">
                    <i className="ri-delete-bin-line text-sm"></i>
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className={`${dark ? 'bg-[#1C2B3A]' : 'bg-white'} rounded-lg p-6 w-80`}>
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-red-500/20 mx-auto mb-4"><i className="ri-delete-bin-line text-red-400 text-2xl"></i></div>
            <h3 className={`text-center font-bold text-base mb-2 ${text}`}>Delete Special Day?</h3>
            <p className={`text-center text-sm ${muted} mb-6`}>This action cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className={`flex-1 py-2.5 rounded-lg text-sm font-semibold cursor-pointer border transition-colors ${dark ? 'border-white/20 text-white/70 hover:bg-white/10' : 'border-gray-200 text-gray-600 hover:bg-gray-100'}`}>Cancel</button>
              <button onClick={() => { deleteSpecialDay(deleteConfirm); setDeleteConfirm(null); }} className="flex-1 py-2.5 rounded-lg text-sm font-semibold bg-red-500 text-white hover:bg-red-600 transition-colors cursor-pointer">Delete</button>
            </div>
          </div>
        </div>
      )}
      {modalOpen && <SpecialDayFormModal day={editing} dark={dark} onClose={() => { setModalOpen(false); setEditing(null); }} />}
    </div>
  );
}

/* ─── Page ─── */
function CampaignsPageContent() {
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const [tab, setTab] = useState<PageTab>('campaigns');

  const text = dark ? 'text-white' : 'text-gray-900';
  const muted = dark ? 'text-white/50' : 'text-gray-500';

  return (
    <div className="space-y-5">
      {/* Tab switcher */}
      <div className={`flex gap-1 p-1 rounded-lg w-fit ${dark ? 'bg-white/5' : 'bg-gray-100'}`}>
        {([
          { key: 'campaigns', label: 'Discount Campaigns', icon: 'ri-megaphone-line' },
          { key: 'special-days', label: 'Special Days', icon: 'ri-calendar-event-line' },
        ] as { key: PageTab; label: string; icon: string }[]).map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
              tab === t.key ? 'bg-[#F97316] text-white' : dark ? 'text-white/50 hover:text-white' : 'text-gray-500 hover:text-gray-900'
            }`}
          >
            <div className="w-4 h-4 flex items-center justify-center"><i className={t.icon}></i></div>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'campaigns' && <CampaignsTab dark={dark} />}
      {tab === 'special-days' && <SpecialDaysTab dark={dark} />}
    </div>
  );
}

export default function AdminCampaignsPage() {
  return (
    <AdminLayout title="Campaigns & Special Days">
      <CampaignsPageContent />
    </AdminLayout>
  );
}
